import { print } from 'graphql'
import {
  buildApolloArgs,
  buildArgs,
  buildFields,
  buildGqlQuery,
  buildMetaArgs,
  buildVariables as defaultBuildVariables,
  getResponseParser,
} from 'ra-data-hasura'
import getFinalType from './getFinalType'

const buildGqlQueryDefaults = {
  buildFields,
  buildMetaArgs,
  buildArgs,
  buildApolloArgs,
  // ra-data-hasura grew a sixth buildGqlQuery parameter; without it the row
  // count subquery is built from `undefined(...)` and every list throws
  // "aggregateFieldName is not a function". This is the package's own default
  // — returning 'NO_COUNT' would skip the count entirely.
  aggregateFieldName: (resourceName: string) => `${resourceName}_aggregate`,
}

const castParams = (params, resource) => {
  const { id, target = 'id' } = params
  if (!id) return

  const typedParam = resource.type.fields.find((f) => target === f.name)
  if (!typedParam) return

  switch (getFinalType(typedParam.type).name) {
    case 'Int':
      params[target] = parseInt(params[target])
      break
  }
}

const lookupResource = async (fetchIntrospection, resourceName, forced = false) => {
  const introspectionResults = await fetchIntrospection(forced)
  console.log(introspectionResults)
  const resource = introspectionResults.resources.find((r) => r.type.name === resourceName)

  if (!resource) {
    if (!forced) {
      return lookupResource(fetchIntrospection, resourceName, true)
    }
    throw new Error(
      `Unknown resource ${resourceName}. No resources were found. Make sure it has been declared on your server side schema and check if your Authorization header is properly set up.`
    )
  }

  return { introspectionResults, resource }
}

/**
 * Which text columns the free-text search (`q`) looks at, per resource.
 * Anything not listed searches all of its own text columns.
 */
const SEARCH_FIELDS: Record<string, string[]> = {
  customers: ['firstname', 'lastname', 'company', 'birth_name'],
  boats: ['name', 'manufacturer', 'model', 'build_number'],
}

/**
 * React-admin's search-as-you-type inputs (AutocompleteInput under a
 * ReferenceInput, list search boxes) send what was typed as `filter.q`.
 * ra-data-hasura has no notion of that and filters on a column called `q`,
 * which Hasura rejects — every such picker came back empty. Translate it:
 * each word typed must match one of the searchable columns, case-insensitively,
 * so "max muster" finds Max Mustermann.
 */
const withFullTextSearch = (params, resource) => {
  const q = params?.filter?.q
  if (q === undefined) return params
  const { q: _, ...filter } = params.filter
  const words = String(q).trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return { ...params, filter }

  const textual = (f) => ['String', 'citext'].includes(getFinalType(f.type).name)
  const fields =
    SEARCH_FIELDS[resource.type.name] ??
    resource.type.fields.filter((f) => textual(f) && !f.name.startsWith('__')).map((f) => f.name)
  if (fields.length === 0) return { ...params, filter }

  return {
    ...params,
    filter: {
      ...filter,
      // ra-data-hasura passes a 'hasura-raw-query' value through as written.
      _and: {
        format: 'hasura-raw-query',
        value: words.map((word) => ({
          _or: fields.map((field) => ({ [field]: { _ilike: `%${word}%` } })),
        })),
      },
    },
  }
}

export const buildQuery =
  ({ queries, ...options }) =>
  (fetchIntrospection) =>
  async (
    aorFetchType,
    resourceName,
    params,
    fileStorage // TODO: think of better (separated) handling of file uploads
  ) => {
    const { introspectionResults, resource } = await lookupResource(
      fetchIntrospection,
      resourceName
    )
    const queryType = resource[aorFetchType]

    if (!queryType) {
      throw new Error(
        `No query or mutation matching fetch type ${aorFetchType} could be found for resource ${resource.type.name}`
      )
    }

    const resourceQuery = (queries[resourceName] || {})[aorFetchType] || {}
    let {
      extendFields,
      buildFields,
      buildMetaArgs,
      buildArgs,
      buildApolloArgs,
      aggregateFieldName,
    } = {
      ...buildGqlQueryDefaults,
      ...options,
      ...resourceQuery,
    }
    let { query, variables, parseResponse } = resourceQuery

    // some helpers - shortcuts
    if (typeof buildFields === 'object' && buildFields.kind === 'Document') {
      buildFields = (
        (gqlObject: any) => () =>
          gqlObject.definitions[0].selectionSet.selections
      )(buildFields)
    }

    // cast fields
    // util fixed: https://github.com/hasura/ra-data-hasura/issues/50
    castParams(params, resource)
    params = withFullTextSearch(params, resource)

    variables = await (variables || defaultBuildVariables)(introspectionResults)(
      resource,
      aorFetchType,
      params,
      queryType,
      fileStorage
    )
    // Tables nhost tracks (auth.users) name their count field `usersAggregate`,
    // not `users_aggregate`; use whichever the schema has.
    const configuredAggregate = aggregateFieldName
    const aggregateName = (name: string) => {
      const configured = configuredAggregate(name)
      return introspectionResults.queries.some((q) => q.name === configured)
        ? configured
        : `${name}Aggregate`
    }
    query = (query || buildGqlQuery)(
      introspectionResults,
      buildFields,
      buildMetaArgs,
      buildArgs,
      buildApolloArgs,
      aggregateName
    )(resource, aorFetchType, queryType, variables)
    // ra-data-hasura renamed defaultGetResponseParser -> getResponseParser.
    parseResponse = (parseResponse || getResponseParser)(introspectionResults)(
      aorFetchType,
      resource,
      queryType
    )

    // auto extend graphql query
    if (Array.isArray(extendFields)) {
      const resourceFields = resource.type.fields.map((f) => f.name)
      // todo: first selection should be the resourceName -- maybe check?
      const selections = query.definitions[0].selectionSet.selections[0].selectionSet.selections
      const selectionFields = selections.map((s) => s.name.value)
      // extend fields
      selections.push(
        ...extendFields
          // check if fields are to be added
          .filter((field) => field[aorFetchType] && !(field.ignore || []).includes(resourceName))
          // flatten all fields to add
          .map((field) => field[aorFetchType].definitions[0].selectionSet.selections)
          .flat()
          // only fetch fields supported by the resource type
          .filter((selection) => resourceFields.includes(selection.name.value))
          // apperently the previous query is reused (cashed) so we prevent adding fields twice
          // skip if already defined
          .filter((selection) => !selectionFields.includes(selection.name.value))
      )
    }

    console.groupCollapsed(`📯 ${resourceName} ${aorFetchType} `)
    console.debug(resource.type)
    console.groupCollapsed(`❓ query`)
    console.debug(query)
    console.debug(print(query))
    console.groupEnd()
    console.groupCollapsed(`📦 variables`)
    console.debug(variables)
    console.debug(JSON.stringify(variables))
    console.groupEnd()
    console.groupCollapsed(`💽 form data`)
    console.debug(params)
    console.groupEnd()
    console.groupEnd()
    parseResponse = ((parseResponse) => (introspectionResults) => {
      const result = parseResponse(introspectionResults)
      console.debug(`💚 ${resourceName} ${aorFetchType} `, introspectionResults, result)
      return result
    })(parseResponse)
    return {
      query,
      variables,
      parseResponse,
    }
  }

export default buildQuery
